import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        secondaryOutline:
          "bg-background border-2 border-primary text-primary hover:bg-primary/5 hover:border-primary",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        pastelBlue: "bg-pastel-blue text-foreground hover:bg-pastel-blue-hover",
        pastelPurple: "bg-pastel-purple text-foreground hover:bg-pastel-purple-hover",
        pastelPink: "bg-pastel-pink text-foreground hover:bg-pastel-pink-hover",
        pastelGreen: "bg-pastel-green text-foreground hover:bg-pastel-green-hover",
        pastelYellow: "bg-pastel-yellow text-foreground hover:bg-pastel-yellow-hover",
        pastelOrange: "bg-pastel-orange text-foreground hover:bg-pastel-orange-hover",
        pastelTeal: "bg-pastel-teal text-foreground hover:bg-pastel-teal-hover",
        darkBlue: "bg-foreground text-background hover:bg-foreground/90",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-7 rounded-md px-3",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
